/**
 * Journal Hooks
 *
 * React Query hooks for journal post and category management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { journalApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type {
  CreateJournalCategoryData,
  UpdateJournalCategoryData,
  CreateJournalPostData,
  UpdateJournalPostData,
} from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const journalKeys = {
  all: ['journal'] as const,
  categories: () => [...journalKeys.all, 'categories'] as const,
  posts: () => [...journalKeys.all, 'posts'] as const,
  postList: (filters: Record<string, unknown>) => [...journalKeys.posts(), 'list', filters] as const,
  postDetail: (id: number) => [...journalKeys.posts(), 'detail', id] as const,
}

// =============================================================================
// Category Hooks
// =============================================================================

export function useJournalCategories() {
  return useQuery({
    queryKey: journalKeys.categories(),
    queryFn: journalApi.getCategories,
  })
}

export function useCreateJournalCategory() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateJournalCategoryData) => journalApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.categories() })
      toast({
        title: 'Category Created',
        description: 'Your journal category has been created.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create category',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateJournalCategory() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateJournalCategoryData }) =>
      journalApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.categories() })
      toast({
        title: 'Category Updated',
        description: 'Your journal category has been updated.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update category',
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteJournalCategory() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => journalApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.categories() })
      toast({
        title: 'Category Deleted',
        description: 'Your journal category has been removed.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete category',
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Post Hooks
// =============================================================================

export function useJournalPosts(params?: {
  categoryId?: number
  featured?: boolean
  published?: boolean
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: journalKeys.postList(params || {}),
    queryFn: () => journalApi.getPosts(params),
  })
}

export function useJournalPost(id: number) {
  return useQuery({
    queryKey: journalKeys.postDetail(id),
    queryFn: () => journalApi.getPost(id),
    enabled: !!id,
  })
}

export function useCreateJournalPost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateJournalPostData) => journalApi.createPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.posts() })
      toast({
        title: 'Post Created',
        description: 'Your journal post has been created.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create post',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateJournalPost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateJournalPostData }) =>
      journalApi.updatePost(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: journalKeys.postDetail(variables.id) })
      queryClient.invalidateQueries({ queryKey: journalKeys.posts() })
      // Going live and coming down are the two changes worth naming. "Post updated" after
      // clicking Publish leaves the artist unsure whether anything is public yet.
      const published = variables.data?.published
      toast(
        published === true
          ? { title: 'Published', description: 'This post is now live on your site.' }
          : published === false
            ? { title: 'Unpublished', description: 'This post is a draft again. Nobody can read it.' }
            : { title: 'Saved', description: 'Your changes are stored.' }
      )
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update post',
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteJournalPost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => journalApi.deletePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.posts() })
      toast({
        title: 'Post Deleted',
        description: 'Your journal post has been removed.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete post',
        variant: 'destructive',
      })
    },
  })
}
